// NseSectorCard.js
// Displays live NSE sector index data fetched via the getNseSectors Cloud Function.
// Fully custom layout — no TradingView widget — so column widths work on all screen sizes.
// Supports interactive column sorting (click header to sort asc/desc).

import React, { useState, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import './NseSectorCard.css';

const fmt = (n, decimals = 2) =>
    n == null ? '—' : Number(n).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

// Maps sort key → CSS column class (th and td must share the same class for alignment)
const COL_CLASS = {
    name:      'nse-col-name',
    price:     'nse-col-price',
    change:    'nse-col-chg',
    changePct: 'nse-col-pct',
};

// Sort arrow — placed LEFT of label for right-aligned numeric columns so the
// arrow sits at the column edge and the label stays flush right.
const SortArrow = ({ col, sortKey, sortDir, numeric }) => {
    const active = sortKey === col;
    const arrow  = !active ? '↕' : sortDir === 'asc' ? '↑' : '↓';
    const cls    = `nse-sort-arrow${active ? ' nse-sort-active' : ' nse-sort-inactive'}`;
    // numeric cols: arrow before label | name col: arrow after label
    return numeric
        ? <span className={cls} style={{ marginRight: 3 }}>{arrow}</span>
        : <span className={cls} style={{ marginLeft: 3 }}>{arrow}</span>;
};

const NseSectorCard = ({ sectors, loading, error, fetchedAt = null }) => {
    const [sortKey, setSortKey] = useState('changePct');  // default: sort by % change
    const [sortDir, setSortDir] = useState('desc');       // desc = biggest gainers first

    const handleSort = (col) => {
        if (sortKey === col) {
            if (sortDir === 'desc') {
                setSortDir('asc');
            } else {
                // Third click resets to default order
                setSortKey(null);
                setSortDir('desc');
            }
        } else {
            setSortKey(col);
            setSortDir('desc');
        }
    };

    const sortedSectors = useMemo(() => {
        if (!sectors) return [];
        if (!sortKey) return sectors;

        return [...sectors].sort((a, b) => {
            let aVal = a[sortKey];
            let bVal = b[sortKey];

            // Nulls always sink to the bottom
            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;

            if (sortKey === 'name') {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
                if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
                return 0;
            }

            return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
        });
    }, [sectors, sortKey, sortDir]);

    if (loading) {
        return (
            <div className="nse-sector-loading">
                <Spinner animation="border" size="sm" className="me-2" />
                <span>Loading sector data…</span>
            </div>
        );
    }

    if (error || !sectors || sectors.length === 0) {
        return null;
    }

    const thCls = (col) =>
        `${COL_CLASS[col]} nse-th-sortable${sortKey === col ? ' nse-th-active' : ''}`;

    return (
        <div className="nse-sector-table-wrap">
            <table className="nse-sector-table">
                <thead>
                    <tr>
                        {/* Name — arrow after label, left-aligned */}
                        <th className={thCls('name')} onClick={() => handleSort('name')}>
                            Sector
                            <SortArrow col="name" sortKey={sortKey} sortDir={sortDir} numeric={false} />
                        </th>
                        {/* Numeric cols — arrow before label, right-aligned */}
                        <th className={thCls('price')} onClick={() => handleSort('price')}>
                            <SortArrow col="price" sortKey={sortKey} sortDir={sortDir} numeric />
                            Price
                        </th>
                        <th className={thCls('change')} onClick={() => handleSort('change')}>
                            <SortArrow col="change" sortKey={sortKey} sortDir={sortDir} numeric />
                            Chg
                        </th>
                        <th className={thCls('changePct')} onClick={() => handleSort('changePct')}>
                            <SortArrow col="changePct" sortKey={sortKey} sortDir={sortDir} numeric />
                            Chg %
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {sortedSectors.map((s) => {
                        const hasChange = s.changePct != null;
                        const positive  = hasChange && s.changePct >= 0;
                        const cls       = !hasChange ? '' : positive ? 'nse-up' : 'nse-down';
                        const sign      = !hasChange ? '' : positive ? '+' : '';
                        return (
                            <tr key={s.name}>
                                <td className="nse-col-name">{s.name}</td>
                                <td className="nse-col-price">{fmt(s.price, 0)}</td>
                                <td className={`nse-col-chg ${cls}`}>
                                    {s.change != null ? `${sign}${fmt(s.change, 0)}` : '—'}
                                </td>
                                <td className={`nse-col-pct ${cls}`}>
                                    {hasChange ? `${sign}${fmt(s.changePct, 2)}%` : '—'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <p className="nse-sector-source">
                {fetchedAt
                    ? `Snapshot as of ${new Date(fetchedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                    : 'Data via NSE India · live during market hours'}
            </p>
        </div>
    );
};

export default NseSectorCard;
